<ng-container *ngIf="state$ | async as state">
  <div class="copilot-chat d-flex flex-column" [class.copilot-chat--drawer-open]="drawer !== null">
    <!-- The summary of the insight and the Actions comes first; the thread and the footer sit at the bottom. The full insight and actions open in the drawer over the chat (prototype D). -->
    <attendance-copilot-summary [attendanceClosed]="attendanceClosed" (open)="openDrawer($event)" />

    <!-- Thread -->
    <div #thread class="copilot-chat__thread mt-auto">
      @if (displayableMessages(state).length === 0 && !state.loading) {
        <div class="copilot-chat__empty text-center text-muted py-4">
          <i
            class="ri-robot-2-line fs-3 d-block mb-2 text-primary opacity-50"
          ></i>
          <p class="mb-0 small">
            {{ "attendance.copilot.emptyState" | translate }}
          </p>
        </div>
      }

      @if (state.loading) {
        <div class="copilot-chat__loading text-center text-muted py-3">
          <span
            class="spinner-border spinner-border-sm text-primary me-2"
            role="status"
          ></span>
          <span class="small">
            {{ "attendance.copilot.loadingHistory" | translate }}
          </span>
        </div>
      }

      @for (
        msg of displayableMessages(state);
        track msg.requestId + ":" + msg.side;
        let i = $index
      ) {
        @if (msg.side === "attendant") {
          <div class="copilot-chat__row copilot-chat__row--attendant">
            <div class="copilot-chat__bubble copilot-chat__bubble--attendant">
              @if (msg.action === "ask") {
                <p class="mb-0">{{ msg.instruction }}</p>
              } @else {
                <p class="mb-0">{{ labelFor(msg.action) | translate }}</p>
              }
            </div>
          </div>
        }

        @if (msg.side === "copilot") {
          <div class="copilot-chat__row copilot-chat__row--copilot">
            @if (isStreakHead(displayableMessages(state), i)) {
              <div class="copilot-chat__avatar">
                <i class="ri-robot-2-line"></i>
              </div>
            } @else {
              <div
                class="copilot-chat__avatar copilot-chat__avatar--ghost"
              ></div>
            }

            <div class="copilot-chat__bubble copilot-chat__bubble--copilot">
              @if (msg.status === "pending") {
                <copilot-thinking-indicator
                  [action]="msg.action"
                ></copilot-thinking-indicator>
              }

              @if (msg.status === "error") {
                <div class="text-danger small mb-2">
                  <i class="ri-error-warning-line me-1"></i
                  >{{
                    (msg.errorMessage | translate) ||
                      ("attendance.copilot.somethingWentWrong" | translate)
                  }}
                </div>
                <button
                  type="button"
                  class="btn btn-sm btn-soft-danger"
                  (click)="retry(msg)"
                >
                  <i class="ri-refresh-line me-1"></i
                  >{{ "attendance.copilot.retry" | translate }}
                </button>
              }

              @if (msg.status === "success" && msg.result) {
                @switch (msg.action) {
                  @case ("suggestion") {
                    @if (suggestionsOf(msg).length) {
                      @for (s of suggestionsOf(msg); track $index) {
                        <div class="copilot-chat__suggestion-item">
                          <p class="copilot-chat__answer mb-0">{{ s.text }}</p>
                          <div
                            class="d-flex justify-content-between align-items-center mt-2 gap-2"
                          >
                            <div class="d-flex align-items-center gap-2">
                              @if (s.toneLabel || s.tone) {
                                <span class="badge bg-soft-info text-info">{{
                                  s.toneLabel || s.tone
                                }}</span>
                              }
                              @if (s.rank) {
                                <span class="text-muted">#{{ s.rank }}</span>
                              }
                            </div>
                            <button
                              type="button"
                              class="btn btn-sm btn-soft-primary copilot-chat__add-btn"
                              [matTooltip]="
                                'attendance.copilot.addToChat' | translate
                              "
                              matTooltipPosition="above"
                              [attr.aria-label]="
                                'attendance.copilot.addToChat' | translate
                              "
                              (click)="addToChat(s.text)"
                            >
                              <i class="ri-add-line"></i>
                            </button>
                          </div>
                        </div>
                      }
                    }
                  }
                  @case ("playbook") {
                    @if (msg.result["summary"]) {
                      <p class="copilot-chat__answer mb-2">
                        {{ msg.result["summary"] }}
                      </p>
                    }
                    <div class="small">
                      <div class="mb-1">
                        <strong>{{
                          "attendance.copilot.currentStage" | translate
                        }}</strong>
                        {{ msg.result["currentStep"] || "—" }}
                      </div>
                      <div class="mb-1">
                        <strong>{{
                          "attendance.copilot.nextStage" | translate
                        }}</strong>
                        {{ msg.result["nextStep"] || "—" }}
                      </div>
                      @if (msg.result["guidance"]) {
                        <p class="mb-1 text-muted mb-0">
                          {{ msg.result["guidance"] }}
                        </p>
                      }
                      @if (msg.result["adherenceScore"] !== undefined) {
                        <span class="badge bg-soft-success text-success mt-1">
                          {{ "attendance.copilot.adherence" | translate }}
                          {{ msg.result["adherenceScore"] }}%
                        </span>
                      }
                    </div>
                  }
                  @case ("health") {
                    @if (msg.result["summary"]) {
                      <p class="copilot-chat__answer mb-0">
                        {{ msg.result["summary"] }}
                      </p>
                    }
                    <div class="small">
                      <div class="d-flex gap-2 flex-wrap mb-1 mt-2">
                        @if (msg.result["healthScore"] !== undefined) {
                          <span class="badge bg-soft-primary text-primary"
                            >{{ "attendance.copilot.score" | translate }}
                            {{ msg.result["healthScore"] }}</span
                          >
                        }
                        @if (msg.result["churnRisk"]) {
                          <span class="badge bg-soft-danger text-danger"
                            >{{ "attendance.copilot.churn" | translate }}
                            {{ msg.result["churnRisk"] }}</span
                          >
                        }
                        @if (msg.result["risk"]) {
                          <span class="badge bg-soft-warning text-warning"
                            >{{ "attendance.copilot.risk" | translate }}
                            {{ msg.result["risk"] }}</span
                          >
                        }
                      </div>
                    </div>
                  }
                  @case ("sentiment") {
                    @if (msg.result["summary"]) {
                      <p class="copilot-chat__answer mb-0">
                        {{ msg.result["summary"] }}
                      </p>
                    }
                    @if (msg.result["averageScore"] !== undefined) {
                      <span class="badge bg-soft-info text-info mt-2">
                        {{ "attendance.copilot.averageScore" | translate }}
                        {{ sentimentScore(msg) }}/10
                      </span>
                    }
                  }
                  @case ("ask") {
                    <p class="copilot-chat__answer mb-0">
                      {{ msg.result["answer"] }}
                    </p>
                  }
                }
              }
            </div>
          </div>
        }
      }

      <div #bottomSentinel class="copilot-chat__sentinel"></div>
    </div>

    <!-- "New message" pill (only when scrolled up) -->
    @if (
      !state.scrollAnchored &&
      state.pendingRequestIds.length === 0 &&
      hasMessages(state)
    ) {
      <button
        type="button"
        class="copilot-chat__new-pill btn btn-sm btn-primary rounded-pill"
        (click)="scrollToBottom(true)"
      >
        <i class="ri-arrow-down-line me-1"></i
        >{{ "attendance.copilot.newMessage" | translate }}
      </button>
    }

    <!-- Footer: the composer, whose quick questions open from the button at its left — hidden entirely for closed attendances (chat stays read-only) -->
    @if (!attendanceClosed) {
      <div class="copilot-chat__footer">
        @if (copilotReady) {
          <div
            class="copilot-chat__composer"
            (keydown.escape)="closeQuickQuestions(true)"
          >
            @if (quickQuestionsOpen) {
              <div
                class="dropdown-menu show copilot-chat__quick-menu"
                role="group"
                [attr.aria-label]="'attendance.copilot.quickQuestions' | translate"
                (click)="$event.stopPropagation()"
              >
                <h6 class="dropdown-header font-12 fw-bold text-muted">
                  {{ "attendance.copilot.quickQuestions" | translate }}
                </h6>
                @for (action of actions; track action.value) {
                  <button
                    type="button"
                    class="dropdown-item copilot-chat__quick-item"
                    [disabled]="
                      !state.idAttendance || isActionPending(state, action.value)
                    "
                    (click)="askQuickQuestion(action.value)"
                  >
                    <span class="copilot-chat__quick-item__icon">
                      <i [class]="action.icon"></i>
                    </span>
                    <span class="text-dark">{{ action.label | translate }}</span>
                  </button>
                }
              </div>
            }
            <button
              #quickToggle
              type="button"
              class="btn btn-sm copilot-chat__quick-toggle"
              [class.btn-primary]="quickQuestionsOpen"
              [class.btn-soft-primary]="!quickQuestionsOpen"
              [attr.aria-expanded]="quickQuestionsOpen"
              [attr.aria-label]="'attendance.copilot.quickQuestions' | translate"
              [matTooltip]="'attendance.copilot.quickQuestions' | translate"
              matTooltipPosition="above"
              [disabled]="!state.idAttendance"
              (click)="toggleQuickQuestions($event)"
            >
              <i class="ri-flashlight-line"></i>
            </button>
            <textarea
              #composer
              class="form-control"
              rows="1"
              placeholder="{{
                'attendance.copilot.composerPlaceholder' | translate
              }}"
              [value]="state.instructionDraft"
              (input)="onInstructionChange($any($event.target).value)"
              (keydown)="onComposerKeydown($event)"
              [disabled]="!state.idAttendance"
            ></textarea>
            <button
              type="button"
              class="btn btn-primary btn-sm copilot-chat__send"
              [disabled]="
                !state.idAttendance || !state.instructionDraft?.trim()
              "
              (click)="askCopilot()"
              [attr.aria-label]="'attendance.copilot.sendQuestion' | translate"
            >
              <i class="ri-send-plane-fill"></i>
            </button>
          </div>
        } @else {
          <div
            class="alert alert-info d-flex align-items-center mb-0"
            role="alert"
          >
            <i class="ri-lock-2-line fs-4 me-2"></i>
            <div
              class="small"
              [innerHTML]="
                'attendance.copilot.lockedHint'
                  | translate: { count: copilotMinMessages }
              "
            ></div>
          </div>
        }
      </div>
    }

    @if (drawer; as pane) {
      <attendance-copilot-drawer
        [pane]="pane"
        [attendanceClosed]="attendanceClosed"
        (paneChange)="switchPane($event)"
        (closed)="closeDrawer()"
      />
    }
  </div>
</ng-container>
